import { useMemo } from "react"
import { usePortfolios } from "resources/portfolios"
import { useETFs, useIndexes } from "resources/instruments"
import { useWatchlists } from "resources/watchlists"
import { useBaskets } from "resources/baskets"
import { type FilterOption } from "resources/filter-ons"
import { ASSET_TYPES_MAP } from "helpers/assets/constants"
import { OK, combineStatuses, type DataStatus, type ResourceResult } from "helpers/data-statuses"

interface Index {
  id: string
  name: string
}

export default function useUniverseFilterOns(modelId: string): ResourceResult<{
  portfoliosList: FilterOption[]
  etfsList: FilterOption[]
  basketsList: FilterOption[]
  indexesList: FilterOption[]
  watchlistsList: FilterOption[]
} | null> {
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [watchlists, watchlistsStatus] = useWatchlists()
  const [etfs, etfsStatus] = useETFs(modelId)
  const [baskets, basketsStatus] = useBaskets()
  const [indexes, indexesStatus]: ResourceResult<Index[]> = useIndexes()

  const portfoliosList = useMemo(() => {
    if (portfoliosStatus !== OK || !portfolios) {
      return []
    }
    return portfolios.map(portfolio => ({ label: portfolio.name, value: portfolio.id, type: ASSET_TYPES_MAP.PORTFOLIO })) || []
  }, [portfolios, portfoliosStatus])

  const basketsList = useMemo(() => {
    if (basketsStatus !== OK || !baskets) {
      return []
    }
    return baskets.map(basket => ({ label: basket.ticker, value: basket.ticker, type: ASSET_TYPES_MAP.BASKET })) || []
  }, [baskets, basketsStatus])

  const indexesList = useMemo(() => {
    if (indexesStatus !== OK || !indexes) {
      return []
    }
    return indexes.map(index => ({ label: index.name, value: index.id, type: ASSET_TYPES_MAP.INDEX })) || []
  }, [indexes, indexesStatus])

  const etfsList = useMemo(() => {
    if (etfsStatus !== OK || !etfs || !Array.isArray(etfs)) {
      return []
    }
    return etfs.map(etf => ({ label: etf.description, value: `${etf.ticker}.${etf.mic}`, type: ASSET_TYPES_MAP.ETF, ticker: etf.ticker, mic: etf.mic })) || []
  }, [etfs, etfsStatus])

  const watchlistsList = useMemo(() => {
    if (watchlistsStatus !== OK || !watchlists) {
      return []
    }
    return watchlists.map(watchlist => ({ label: watchlist.name, value: watchlist.id, type: ASSET_TYPES_MAP.WATCHLIST })) || []
  }, [watchlists, watchlistsStatus])

  const status = combineStatuses([portfoliosStatus, basketsStatus, indexesStatus, etfsStatus, watchlistsStatus] as DataStatus[])

  return useMemo(() => {
    if (status !== OK) {
      return [null, status]
    }
    return [
      { portfoliosList, basketsList, indexesList, etfsList, watchlistsList },
      status,
    ]
  }, [portfoliosList, basketsList, indexesList, etfsList, watchlistsList, status])
}
